import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import { EmptyState } from './primitives';
import { ChartBar } from '@phosphor-icons/react';

/** Kapil brand–aligned chart palette (not Horilla orange). */
export const CHART_COLORS = [
  '#002FA7',
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#8B5CF6',
  '#EC4899',
  '#06B6D4',
  '#64748B',
];

const STATUS_COLORS = {
  open: '#F59E0B',
  in_progress: '#3B82F6',
  pending: '#8B5CF6',
  completed: '#10B981',
  closed: '#64748B',
  cancelled: '#EF4444',
  reopen: '#EC4899',
};

function ChartCard({ title, subtitle, children, height = 260, autoHeight = false, headerRight = null }) {
  return (
    <div className={`wd-chart-card tk-surface tk-surface-pad${autoHeight ? ' is-auto-height' : ''}`}>
      {(title || headerRight) ? (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: subtitle ? 0 : 4 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {title ? <h3 className="wd-chart-title" style={{ marginBottom: subtitle ? undefined : 0 }}>{title}</h3> : null}
          </div>
          {headerRight}
        </div>
      ) : null}
      {subtitle ? <p className="wd-chart-subtitle">{subtitle}</p> : null}
      <div
        className={`wd-chart-body${autoHeight ? ' is-scroll' : ''}`}
        style={autoHeight ? undefined : { minHeight: 0 }}
      >
        {autoHeight ? children : (
          <div style={{ width: '100%', height: '100%', minHeight: height ? undefined : 200 }}>
            {children}
          </div>
        )}
      </div>
    </div>
  );
}

function normalizePieData(data) {
  if (!data) return [];
  if (Array.isArray(data)) {
    return data
      .map((d) => ({
        name: d.name || d.label || String(d.key || ''),
        value: Number(d.value ?? d.count ?? 0),
        key: d.key || d.name || d.label,
      }))
      .filter((d) => d.value > 0 && d.name);
  }
  return Object.entries(data)
    .map(([key, value]) => ({
      name: key.replace(/_/g, ' '),
      value: Number(value) || 0,
      key,
    }))
    .filter((d) => d.value > 0);
}

const SLA_COLORS = {
  breached: '#DC2626',
  at_risk: '#D97706',
  on_track: '#10B981',
  assignment_breached: '#9D174D',
  assignment_at_risk: '#3730A3',
};

/**
 * Pictorial SLA risk chart (donut). Click a slice to drill into SLA Management.
 * data: { breached, at_risk, on_track } or array of { key, name, value }
 */
export function SlaRiskDonut({
  data,
  title = 'SLA risk',
  subtitle = 'Click a segment to open SLA Management',
  onSliceClick,
  height = 260,
}) {
  const rows = normalizePieData(data).map((row) => ({
    ...row,
    name: row.name.replace(/_/g, ' '),
  }));
  if (!rows.length) {
    return (
      <ChartCard title={title} subtitle={subtitle} height={height}>
        <EmptyState icon={ChartBar} title="No SLA data" description="No open tickets with SLA yet." />
      </ChartCard>
    );
  }

  return (
    <ChartCard title={title} subtitle={subtitle} height={height}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={rows}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius="55%"
            outerRadius="80%"
            paddingAngle={2}
            onClick={(entry) => onSliceClick?.(entry?.payload?.key || entry?.name)}
            style={{ cursor: onSliceClick ? 'pointer' : 'default' }}
          >
            {rows.map((row, i) => (
              <Cell
                key={row.key || row.name}
                fill={SLA_COLORS[row.key] || CHART_COLORS[i % CHART_COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

export function StatusDonut({ data, title = 'Tickets by status', onSliceClick, height = 260 }) {
  const rows = normalizePieData(data);
  if (!rows.length) {
    return (
      <ChartCard title={title} height={height}>
        <EmptyState icon={ChartBar} title="No status data" description="Nothing to chart yet." />
      </ChartCard>
    );
  }

  return (
    <ChartCard title={title} height={height}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={rows}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius="55%"
            outerRadius="80%"
            paddingAngle={2}
            onClick={(entry) => onSliceClick?.(entry?.payload?.key || entry?.name)}
            style={{ cursor: onSliceClick ? 'pointer' : 'default' }}
          >
            {rows.map((row, i) => (
              <Cell
                key={row.key || row.name}
                fill={STATUS_COLORS[row.key] || CHART_COLORS[i % CHART_COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

export function CategoryPie({ data, title = 'By category', subtitle, onSliceClick, height = 260, headerRight = null }) {
  const rows = normalizePieData(data);
  if (!rows.length) {
    return (
      <ChartCard title={title} subtitle={subtitle} height={height} headerRight={headerRight}>
        <EmptyState icon={ChartBar} title="No category data" description="Nothing to chart yet." />
      </ChartCard>
    );
  }

  return (
    <ChartCard title={title} subtitle={subtitle} height={height} headerRight={headerRight}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
          <Pie
            data={rows}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="42%"
            outerRadius="58%"
            onClick={(entry) => onSliceClick?.(entry?.payload?.key || entry?.name)}
            style={{ cursor: onSliceClick ? 'pointer' : 'default' }}
          >
            {rows.map((row, i) => (
              <Cell key={row.key || row.name} fill={CHART_COLORS[i % CHART_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip />
          <Legend
            verticalAlign="bottom"
            align="center"
            layout="horizontal"
            wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
            formatter={(value) => (value.length > 18 ? `${value.slice(0, 16)}…` : value)}
          />
        </PieChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

/**
 * Volume bar chart.
 * data: [{ date|label, created|count, closed? }]
 */
export function VolumeBar({
  data = [],
  title = 'Volume',
  subtitle,
  createdKey = 'created',
  closedKey = 'closed',
  createdName = 'Created',
  closedName = 'Closed',
  labelKey = 'label',
  height = 260,
  headerRight = null,
  onBarClick = null,
}) {
  const rows = (data || []).map((d) => ({
    ...d,
    label: d[labelKey] || d.date || d.name || '',
    date: d.date || d[labelKey] || '',
    created: Number(d[createdKey] ?? d.count ?? 0),
    closed: Number(d[closedKey] ?? 0),
  }));

  if (!rows.length) {
    return (
      <ChartCard title={title} subtitle={subtitle} height={height} headerRight={headerRight}>
        <EmptyState icon={ChartBar} title="No volume data" description="Nothing to chart yet." />
      </ChartCard>
    );
  }

  const hasClosed = rows.some((r) => r.closed > 0);

  return (
    <ChartCard title={title} subtitle={subtitle} height={height} headerRight={headerRight}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend />
          <Bar
            dataKey="created"
            name={createdName}
            fill="#002FA7"
            radius={[2, 2, 0, 0]}
            cursor={onBarClick ? 'pointer' : 'default'}
            onClick={(entry) => {
              const payload = entry?.payload || entry;
              if (onBarClick) onBarClick(payload?.date || payload?.label, payload);
            }}
          />
          {hasClosed ? (
            <Bar
              dataKey="closed"
              name={closedName}
              fill="#10B981"
              radius={[2, 2, 0, 0]}
              cursor={onBarClick ? 'pointer' : 'default'}
              onClick={(entry) => {
                const payload = entry?.payload || entry;
                if (onBarClick) onBarClick(payload?.date || payload?.label, payload);
              }}
            />
          ) : null}
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

/**
 * Ranked horizontal bars — avoids overlapping Y-axis labels for long product/client names.
 * data: [{ name, value|count|in_progress, key? }]
 */
export function WorkloadBar({
  data = [],
  title = 'Workload by assignee',
  subtitle,
  valueKey,
  valueLabel = 'Count',
  height = 280,
  onBarClick,
  maxItems = 12,
  headerRight = null,
}) {
  const rows = (data || []).map((d) => ({
    name: String(d.name || d.label || d.user_id || 'Unknown'),
    value: Number(d[valueKey] ?? d.in_progress ?? d.count ?? d.value ?? 0),
    key: d.key || d.product_id || d.client || d.domain || d.name,
  })).filter((d) => d.value > 0).slice(0, maxItems);

  if (!rows.length) {
    return (
      <ChartCard title={title} subtitle={subtitle} height={height} headerRight={headerRight}>
        <EmptyState icon={ChartBar} title="No data" description="Nothing to chart yet." />
      </ChartCard>
    );
  }

  const max = Math.max(...rows.map((r) => r.value), 1);

  return (
    <ChartCard title={title} subtitle={subtitle} autoHeight headerRight={headerRight}>
      <div className="wd-ranked-bars" role="list" aria-label={title}>
        {rows.map((row) => {
          const pct = Math.max(4, Math.round((row.value / max) * 100));
          const clickable = Boolean(onBarClick);
          const Tag = clickable ? 'button' : 'div';
          return (
            <Tag
              key={String(row.key || row.name)}
              type={clickable ? 'button' : undefined}
              className={`wd-ranked-bar-row${clickable ? ' is-clickable' : ''}`}
              role="listitem"
              title={`${row.name}: ${row.value}`}
              onClick={clickable ? () => onBarClick?.(row.key || row.name, row) : undefined}
            >
              <div className="wd-ranked-bar-meta">
                <span className="wd-ranked-bar-name">{row.name}</span>
                <span className="wd-ranked-bar-value">
                  {row.value}
                  <span className="wd-ranked-bar-unit">{valueLabel}</span>
                </span>
              </div>
              <div className="wd-ranked-bar-track" aria-hidden>
                <div className="wd-ranked-bar-fill" style={{ width: `${pct}%` }} />
              </div>
            </Tag>
          );
        })}
      </div>
    </ChartCard>
  );
}

export function UtilizationLine({ data = [], title = 'Utilization', height = 260 }) {
  const rows = (data || []).map((d) => ({
    label: d.label || d.date || d.name || '',
    value: Number(d.value ?? d.pct ?? d.utilization ?? 0),
  }));

  if (!rows.length) {
    return (
      <ChartCard title={title} height={height}>
        <EmptyState icon={ChartBar} title="No utilization data" description="Nothing to chart yet." />
      </ChartCard>
    );
  }

  return (
    <ChartCard title={title} height={height}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={rows}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Line type="monotone" dataKey="value" name="Utilization" stroke="#002FA7" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
